import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { LoadingPage } from "@/components/LoadingPage";
import { Breadcrumb } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { Centered } from "@/components/ui/container";
import { Tab, TabPanel, Tabs, TabsList } from "@/components/ui/tabs";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import { ResultDetailForm } from "../components/results/ResultDetailForm";
import { ResultResultSpecsTable } from "../components/results/ResultResultSpecsTable";
import { Section } from "../components/Section";
import { useTabSearch } from "../hooks";

export function EditResultPage(params: { value: string }) {
  const tabsProps = useTabSearch();

  const {
    data: result,
    isPending,
    error,
  } = trpc.admin.testResult.get.useQuery(params);

  if (isPending) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <Centered>
        <Text isDanger>{error.message}</Text>
      </Centered>
    );
  }

  const isOrganism = result.category === "ORGANISM";

  return (
    <>
      <Section.Title
        breadcrumb={
          <>
            <Breadcrumb
              href={{
                to: "/admin/results",
                search: isOrganism ? { t: "organisms" } : undefined,
              }}
            >
              {isOrganism ? "Organisms" : "Test results"}
            </Breadcrumb>
            <Breadcrumb>{result.description}</Breadcrumb>
          </>
        }
        actions={
          <DeleteResultDialog isOrganism={isOrganism} result={result}>
            <Button variant="destructive">Delete</Button>
          </DeleteResultDialog>
        }
      >
        {result.description}
      </Section.Title>
      {isOrganism ? (
        <ResultDetailForm result={result} />
      ) : (
        <Tabs {...tabsProps}>
          <TabsList className="w-full justify-start">
            <Tab id="details">Details</Tab>
            <Tab id="results">Result specs</Tab>
          </TabsList>
          <TabPanel id="details">
            <ResultDetailForm result={result} />
          </TabPanel>
          <TabPanel id="results">
            <ResultResultSpecsTable result={result} />
          </TabPanel>
        </Tabs>
      )}
    </>
  );
}

function DeleteResultDialog({
  children,
  isOrganism,
  result,
}: {
  children: ReactNode;
  isOrganism: boolean;
  result: { value: string };
}) {
  const [open, setOpen] = useState(false);
  const deleteResult = trpc.admin.testResult.delete.useMutation();
  const navigate = useNavigate();

  const onConfirm = () => {
    deleteResult.mutate(
      result,
      mutationResponse({
        errorTitle: "Error deleting test result",
        successMessage: "Test result deleted",
        onSuccess() {
          setOpen(false);
          navigate({
            to: "/admin/results",
            search: isOrganism ? { t: "organisms" } : undefined,
          });
        },
      }),
    );
  };

  return (
    <ConfirmPrompt
      danger
      open={open}
      onOpenChange={setOpen}
      title="Delete test result"
      message="Are you sure you want to delete this test result?"
      onConfirm={onConfirm}
      loading={deleteResult.isPending}
    >
      {children}
    </ConfirmPrompt>
  );
}
